import React, { useState, useEffect, useMemo, useRef } from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { Box, CircularProgress } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import ChatMediaCollapsedPreview from "./ChatMediaCollapsedPreview";
import { useEchoMediaFetch } from "./useEchoMediaFetch";

function ImageViewer({ imageUrl, mediaThumbnailUrl, mediaFileSizeBytes, contactId, messageId, metaMediaId }) {
  const [revealed, setRevealed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { resolvedUrl, isFetching, handleFetch: handleFetchMedia } = useEchoMediaFetch(imageUrl, {
    contactId,
    messageId,
    tag: "ImageViewer",
  });
  const blobUrlRef = useRef(null);

  const isJsonString =
    typeof resolvedUrl === "string" &&
    (resolvedUrl.trim().startsWith("{") || resolvedUrl.trim().startsWith("["));

  const displayUrl = useMemo(() => {
    if (resolvedUrl instanceof File) {
      if (blobUrlRef.current) {
        try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) {}
      }
      const u = URL.createObjectURL(resolvedUrl);
      blobUrlRef.current = u;
      return u;
    }
    return resolvedUrl;
  }, [resolvedUrl]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) {}
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setRevealed(false);
    setHasError(false);
    setIsLoading(false);
  }, [resolvedUrl, displayUrl, mediaThumbnailUrl]);

  if (isJsonString) {
    console.warn("⚠️ ImageViewer received JSON string (likely template body), not rendering image");
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 2, backgroundColor: "#f0f0f0", borderRadius: "8px", minHeight: "150px", minWidth: "150px" }}>
        <ImageIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
        <Box sx={{ color: "#999", fontSize: "12px" }}>Invalid image URL (JSON detected)</Box>
      </Box>
    );
  }

  // No URL yet but we have a metaMediaId — show download button; fetch on click
  if (!resolvedUrl && metaMediaId) {
    return (
      <ChatMediaCollapsedPreview
        thumbnailUrl={null}
        fallbackSrc={null}
        sizeBytes={null}
        onActivate={handleFetchMedia}
        minHeight={200}
        maxHeight={280}
        borderRadius="12px"
        isLoading={isFetching}
      />
    );
  }

  const getBlurPlaceholderUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    if (url.includes("cloudinary.com")) {
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}q=auto:low&w=200&e_blur:300&f_auto`;
    }
    return null;
  };

  const placeholderUrl = typeof displayUrl === "string" ? getBlurPlaceholderUrl(displayUrl) : null;
  const previewForBlur = mediaThumbnailUrl || placeholderUrl;

  const strUrl = typeof displayUrl === "string" ? displayUrl.trim() : "";
  const isRemote = strUrl.startsWith("http://") || strUrl.startsWith("https://");
  const useCollapsedGate = !isJsonString && !revealed && isRemote;

  const sizeBytes =
    mediaFileSizeBytes != null && Number.isFinite(Number(mediaFileSizeBytes))
      ? Number(mediaFileSizeBytes)
      : resolvedUrl instanceof File && Number.isFinite(resolvedUrl.size)
        ? resolvedUrl.size
        : null;

  const handleImageLoad = () => { setIsLoading(false); setHasError(false); };
  const handleImageError = () => { setIsLoading(false); setHasError(true); };
  const activateFull = () => { setIsLoading(true); setRevealed(true); };

  if (useCollapsedGate) {
    return (
      <ChatMediaCollapsedPreview
        thumbnailUrl={previewForBlur}
        fallbackSrc={null}
        sizeBytes={sizeBytes}
        onActivate={activateFull}
        minHeight={200}
        maxHeight={280}
        borderRadius="12px"
      />
    );
  }

  return (
    <PhotoProvider>
      <PhotoView src={displayUrl}>
        {/* Sized up + rounded (Rahim, 2026-08-27: "make the image more big add corner radius
            as well" — was a flush 200px-tall square-cornered image, same cap the collapsed
            gate above already uses `borderRadius`/`overflow:hidden` for, this just brings the
            revealed image in line with it). `overflow:hidden` on the wrapper (not the `<img>`
            itself) keeps the radius clean regardless of the image's natural aspect ratio. */}
        <Box
          sx={{
            position: "relative",
            maxWidth: "calc(var(--chatbox-width, 100vw) * 0.6)",
            width: "auto",
            maxHeight: "280px",
            borderRadius: "12px",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          {isLoading && (
            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {hasError ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 2, backgroundColor: "#f0f0f0", borderRadius: "12px", minHeight: "150px", minWidth: "150px" }}>
              <ImageIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
              <Box sx={{ color: "#999", fontSize: "12px" }}>Failed to load image</Box>
            </Box>
          ) : (
            <img
              src={displayUrl}
              alt="chat-media"
              onLoad={handleImageLoad}
              onError={handleImageError}
              // Hides the broken/pending-image alt-text-as-blue-link glitch (Rahim,
              // 2026-08-27) — `color` only paints the fallback alt text, not the image itself.
              style={{ display: "block", maxWidth: "calc(var(--chatbox-width, 100vw) * 0.6)", width: "auto", maxHeight: "280px", cursor: "pointer", objectFit: "cover", opacity: isLoading ? 0.5 : 1, transition: "opacity 0.3s ease", color: "transparent" }}
            />
          )}
        </Box>
      </PhotoView>
    </PhotoProvider>
  );
}

export default ImageViewer;
